import { useState } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { useIsDemoUser } from "@/shared/hooks/useIsDemoUser";
import { assetDetailStyles } from "@/features/assets/services/assetDetailStyles";
import { PhotoGrid } from "./assetPhotosTab/PhotoGrid";
import { PhotoLightbox } from "./assetPhotosTab/PhotoLightbox";
import { usePhotoSignedUrls } from "./assetPhotosTab/usePhotoSignedUrls";
import type { AssetPhoto, PhotoWithUrl } from "./assetPhotosTab/types";

interface AssetPhotosTabProps {
  photos: AssetPhoto[];
  assetId: string;
  onRefetch: () => void;
}

export function AssetPhotosTab({ photos: propPhotos, assetId, onRefetch }: AssetPhotosTabProps) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const { photosWithUrls, loading } = usePhotoSignedUrls(propPhotos);
  const { toast } = useToast();
  const isDemoUser = useIsDemoUser();

  const denyDemoWrite = () => {
    toast({
      title: "Demo is read-only",
      description: "Editing photos is not available in the demo.",
    });
  };

  const handleSetPrimary = async (photoId: string) => {
    if (isDemoUser) return denyDemoWrite();
    try {
      const photo = photosWithUrls.find((p) => p.id === photoId);
      if (!photo) return;

      const { error: updateError } = await supabase
        .from('property_assets')
        .update({
          primary_photo_id: photoId,
        })
        .eq('id', assetId);

      if (updateError) throw updateError;

      toast({
        title: "Primary photo updated",
        description: "This photo is now the primary image for this asset",
      });

      onRefetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to set primary photo",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (photo: PhotoWithUrl) => {
    if (!photo.signedUrl) return;

    try {
      const response = await fetch(photo.signedUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = photo.document_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download photo",
        variant: "destructive",
      });
    }
  };

  const handleTagPhoto = async (photoId: string, tag: string) => {
    if (isDemoUser) return denyDemoWrite();
    try {
      const currentPhoto = photosWithUrls.find((p) => p.id === photoId);
      const currentTags = currentPhoto?.tags || [];

      // Toggle tag: if already applied, remove it; otherwise set it as the only tag
      const newTags = currentTags.includes(tag) ? [] : [tag];

      const { error } = await supabase
        .from('asset_documents')
        .update({
          tags: newTags.length > 0 ? newTags : null,
        })
        .eq('id', photoId);

      if (error) throw error;

      toast({
        title: newTags.length > 0 ? "Photo tagged" : "Tag removed",
        description: newTags.length > 0 ? `Photo has been marked as ${tag}` : "Photo tag has been removed",
      });

      onRefetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update photo tag",
        variant: "destructive",
      });
    }
  };

  const handleCloseViewer = () => setSelectedPhotoIndex(null);

  const handlePrevPhoto = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const handleNextPhoto = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex < photosWithUrls.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };

  return (
    <div className={assetDetailStyles.sectionSpacing}>
      <PhotoGrid
        photosWithUrls={photosWithUrls}
        loading={loading}
        onSelectPhoto={setSelectedPhotoIndex}
        onSetPrimary={handleSetPrimary}
        onDownload={handleDownload}
      />

      {selectedPhotoIndex !== null && (
        <PhotoLightbox
          photosWithUrls={photosWithUrls}
          selectedIndex={selectedPhotoIndex}
          onClose={handleCloseViewer}
          onPrev={handlePrevPhoto}
          onNext={handleNextPhoto}
          onDownload={handleDownload}
          onTagPhoto={handleTagPhoto}
        />
      )}
    </div>
  );
}
